import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, rp } from "@/lib/api";
import { logError } from "@/lib/log";

const sel = "h-10 rounded-md border border-input bg-white px-3 text-sm";
const STATUSES = ["", "pending", "earned", "paid", "void"];
const CATS = [["", "Semua kategori"], ["external_rental", "Sewa Keluar"],
  ["play_at_venue", "Main di Venue"]];

export default function PartnerCommissions() {
  const [data, setData] = useState(null);
  const [branches, setBranches] = useState([]);
  const [q, setQ] = useState({ branch_id: "", status: "", category: "" });

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => setBranches([]));
  }, []);
  const load = useCallback(() => api.get("/partner-commissions", { params: {
    branch_id: q.branch_id || undefined, status: q.status || undefined,
    category: q.category || undefined } })
    .then((r) => setData(r.data))
    .catch((e) => logError("Komisi partner gagal dimuat", e)), [q]);
  useEffect(() => {
    load();
  }, [load]);

  const markPaid = async (row) => {
    if (!window.confirm(`Tandai komisi ${row.booking_code} (${row.amount}) sudah dibayar?`)) return;
    try {
      await api.post(`/partner-commissions/${row.id}/mark-paid`, {});
      toast.success("Komisi ditandai sudah dibayar");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  return (
    <div data-testid="admin-partner-commissions">
      <h1 className="font-display text-3xl tracking-tight">Komisi Partner</h1>
      <p className="mt-1 text-sm text-ink-soft">
        Komisi Hub dari dua kategori: <b>Sewa Keluar</b> (Pickup & Delivery) dan <b>Main di Venue</b>
        (online maupun dibantu staf partner). Aturan dibekukan saat pembayaran masuk — mengubah
        aturan Hub tidak pernah mengubah komisi booking yang sudah dibayar.
      </p>

      <div className="mt-5 flex flex-wrap gap-3">
        <select className={sel} data-testid="pc-filter-branch" value={q.branch_id}
          onChange={(e) => setQ({ ...q, branch_id: e.target.value })}>
          <option value="">Semua Hub</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select className={sel} data-testid="pc-filter-status" value={q.status}
          onChange={(e) => setQ({ ...q, status: e.target.value })}>
          {STATUSES.map((s) => <option key={s} value={s}>{s || "Semua status"}</option>)}
        </select>
        <select className={sel} data-testid="pc-filter-category" value={q.category}
          onChange={(e) => setQ({ ...q, category: e.target.value })}>
          {CATS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {Object.entries(data?.by_category || {}).map(([k, c]) => (
          <div key={k} className="bg-white border border-black/10 rounded-lg p-4"
            data-testid={`pc-category-${k}`}>
            <div className="text-[11px] uppercase tracking-widest text-ink-soft">{c.label}</div>
            <div className="mt-1 font-display text-2xl">{rp(c.amount)}</div>
            <div className="text-xs text-ink-soft">
              {c.bookings} booking · dasar {rp(c.base)}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-4">
        {[["Terkumpul (belum dibayar)", data?.totals?.earned, "pc-total-earned"],
          ["Sudah dibayar", data?.totals?.paid, "pc-total-paid"],
          ["Menunggu pembayaran booking", data?.totals?.pending, "pc-total-pending"],
          ["Dibatalkan", data?.totals?.void, "pc-total-void"]].map(([label, v, tid]) => (
          <div key={tid} className="bg-white border border-black/10 rounded-lg p-4">
            <div className="text-[11px] uppercase tracking-widest text-ink-soft">{label}</div>
            <div className="mt-1 font-display text-2xl" data-testid={tid}>{rp(v || 0)}</div>
          </div>
        ))}
      </div>

      <h2 className="mt-8 text-base font-semibold">Per Partner</h2>
      <div className="mt-3 overflow-x-auto bg-white border border-black/10 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-cream-dark text-left text-[11px] uppercase tracking-widest text-ink-soft">
            <tr><th className="p-3">Partner</th><th className="p-3">Hub</th><th className="p-3">Booking</th>
              <th className="p-3">Lunas</th><th className="p-3">Menunggu</th></tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {(data?.partners || []).map((p) => (
              <tr key={p.partner_email} data-testid={`pc-partner-${p.partner_email}`}>
                <td className="p-3 font-medium">{p.partner_email}</td>
                <td className="p-3">{p.branch_name}</td>
                <td className="p-3">{p.bookings}</td>
                <td className="p-3">{rp(p.earned + (p.paid || 0))}</td>
                <td className="p-3">{rp(p.pending)}</td>
              </tr>
            ))}
            {(data?.partners || []).length === 0 && <tr><td colSpan={5} className="p-4 text-ink-soft">Belum ada data.</td></tr>}
          </tbody>
        </table>
      </div>

      <h2 className="mt-8 text-base font-semibold">Rincian</h2>
      <div className="mt-3 overflow-x-auto bg-white border border-black/10 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-cream-dark text-left text-[11px] uppercase tracking-widest text-ink-soft">
            <tr><th className="p-3">Booking</th><th className="p-3">Hub</th><th className="p-3">Kategori</th>
              <th className="p-3">Dasar</th><th className="p-3">Aturan</th><th className="p-3">Komisi</th>
              <th className="p-3">Status</th><th className="p-3">Aksi</th></tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {(data?.rows || []).map((c) => (
              <tr key={c.id} data-testid={`pc-row-${c.booking_code}`}>
                <td className="p-3 font-medium">{c.booking_code}
                  <div className="text-xs text-ink-soft">{c.partner_email || "online"}</div></td>
                <td className="p-3">{c.branch_name}</td>
                <td className="p-3 text-xs">{c.category_label || "—"}</td>
                <td className="p-3">{rp(c.base_amount)}</td>
                <td className="p-3 text-xs">{c.breakdown
                  || (c.rule_mode === "percentage" ? `${c.rule_value}%` : rp(c.rule_value))}
                  <div className="text-ink-soft">v{c.rule_version || 0}
                    {c.rule_effective_from ? ` · ${String(c.rule_effective_from).slice(0, 10)}` : ""}</div></td>
                <td className="p-3">{rp(c.amount)}</td>
                <td className="p-3">{c.status}</td>
                <td className="p-3">
                  {c.status === "earned" && (
                    <button data-testid={`pc-mark-paid-${c.booking_code}`} onClick={() => markPaid(c)}
                      className="text-xs text-lm-red hover:underline">Tandai dibayar</button>
                  )}
                </td>
              </tr>
            ))}
            {(data?.rows || []).length === 0 && <tr><td colSpan={8} className="p-4 text-ink-soft">Belum ada komisi.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
